/**
 * Octopus IoT Platform - Geofence Rule Editor
 * FASE 2: Component to edit individual geofence rule triggers
 */
import React from 'react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PhoneListInput } from '@/components/PhoneNumberInput';

// Trigger options with Portuguese labels
const TRIGGER_OPTIONS = [
  { value: 'on_exit', label: 'Ao sair da zona' },
  { value: 'on_enter', label: 'Ao entrar na zona' },
  { value: 'approaching_exit', label: 'Aproximação de saída' },
  { value: 'approaching_enter', label: 'Aproximação de entrada' },
];

// Severity options with colors
const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Baixa', color: '#22C55E' },
  { value: 'medium', label: 'Média', color: '#F59E0B' },
  { value: 'high', label: 'Alta', color: '#FF6B35' },
  { value: 'critical', label: 'Crítica', color: '#EF4444' },
];

/**
 * GeofenceRuleEditor Component
 * 
 * @param {object} rule - Rule configuration object
 * @param {function} onChange - Callback when rule changes
 * @param {function} onDelete - Callback when delete is clicked
 * @param {boolean} compact - Use compact layout
 * @param {string} userRole - Current user's role ('admin', 'user', etc.) for permission checks
 */
const GeofenceRuleEditor = ({ 
  rule, 
  onChange, 
  onDelete,
  compact = false,
  userRole = 'user'
}) => {
  // Check if user is admin (can edit override fields)
  const isAdmin = userRole === 'admin';
  
  // Check if this is a proximity trigger
  const isProximityTrigger = rule.trigger === 'approaching_exit' || rule.trigger === 'approaching_enter';
  
  // Handle field change
  const handleChange = (field, value) => {
    onChange({ ...rule, [field]: value });
  };
  
  // Handle email list change
  const handleEmailsChange = (value) => {
    const emails = value.split(',').map(e => e.trim()).filter(Boolean);
    handleChange('notification_emails', emails);
  };
  
  // Get severity badge color
  const severityConfig = SEVERITY_OPTIONS.find(s => s.value === rule.severity) || SEVERITY_OPTIONS[1];
  
  if (compact) {
    return (
      <div className="p-3 bg-muted/30 rounded-lg space-y-2">
        {/* Row 1: Trigger, Proximity, Severity, Delete */}
        <div className="flex items-center gap-2">
          <Select value={rule.trigger} onValueChange={(v) => handleChange('trigger', v)}>
            <SelectTrigger className="w-[180px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRIGGER_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {isProximityTrigger && (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={rule.proximity_meters || 50}
                onChange={(e) => handleChange('proximity_meters', parseInt(e.target.value) || 50)}
                className="w-16 h-8 text-xs"
                placeholder="50"
              />
              <span className="text-xs text-muted-foreground">m</span>
            </div>
          )}

          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={5}
              value={rule.repeat_interval_minutes || 5}
              onChange={(e) => handleChange('repeat_interval_minutes', Math.max(5, parseInt(e.target.value) || 5))}
              className="w-16 h-8 text-xs"
              title="Intervalo de repetição em minutos"
            />
            <span className="text-xs text-muted-foreground">min</span>
          </div>
          
          <Select value={rule.severity || 'medium'} onValueChange={(v) => handleChange('severity', v)}>
            <SelectTrigger className="w-[100px] h-8 text-xs border-0 bg-transparent p-0 focus:ring-0">
              <Badge 
                style={{ backgroundColor: severityConfig.color }}
                className="text-white text-xs cursor-pointer"
              >
                {severityConfig.label}
              </Badge>
            </SelectTrigger>
            <SelectContent>
              {SEVERITY_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: opt.color }}
                    />
                    {opt.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="ghost" 
            size="icon"
            className="h-8 w-8 text-destructive hover:bg-destructive/10 ml-auto"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Row 2: Notification Settings (Email + WhatsApp) */}
        <div className="space-y-2 pt-2 border-t border-border/50">
          {/* Info text */}
          <p className="text-xs text-muted-foreground italic">
            Todos os usuários cadastrados serão notificados
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Email Notification */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Email</Label>
                <Switch
                  checked={rule.notify_email || false}
                  onCheckedChange={(v) => handleChange('notify_email', v)}
                  data-testid="compact-rule-email-switch"
                  className="scale-75"
                />
              </div>
              {rule.notify_email && (
                <Input
                  placeholder={isAdmin ? "Adicionais (opcional)" : "Somente admin"}
                  value={(rule.notification_emails || []).join(', ')}
                  onChange={(e) => handleEmailsChange(e.target.value)}
                  className={cn("h-7 text-xs", !isAdmin && "opacity-50 cursor-not-allowed")}
                  disabled={!isAdmin}
                  data-testid="compact-rule-emails-input"
                />
              )}
            </div>
            
            {/* WhatsApp Notification */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">WhatsApp</Label>
                <Switch
                  checked={rule.notify_whatsapp || false}
                  onCheckedChange={(v) => handleChange('notify_whatsapp', v)}
                  data-testid="compact-rule-whatsapp-switch"
                  className="scale-75"
                />
              </div>
              {rule.notify_whatsapp && (
                <PhoneListInput
                  value={rule.notification_phones || []}
                  onChange={(phones) => handleChange('notification_phones', phones)}
                  disabled={!isAdmin}
                  idPrefix="compact-rule-phones"
                  compact
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 bg-muted/30 rounded-lg space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 grid grid-cols-2 gap-4">
          {/* Trigger */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Gatilho</Label>
            <Select value={rule.trigger} onValueChange={(v) => handleChange('trigger', v)}>
              <SelectTrigger data-testid="rule-trigger-select">
                <SelectValue placeholder="Selecione o gatilho" />
              </SelectTrigger>
              <SelectContent>
                {TRIGGER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Proximity Meters - Only for approaching triggers */}
          {isProximityTrigger && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Distância (metros)</Label>
              <Input
                type="number"
                value={rule.proximity_meters || 50}
                onChange={(e) => handleChange('proximity_meters', parseInt(e.target.value) || 50)}
                min={1}
                max={10000}
                data-testid="rule-proximity-input"
              />
            </div>
          )}
          
          {/* Severity */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Severidade</Label>
            <Select value={rule.severity || 'medium'} onValueChange={(v) => handleChange('severity', v)}>
              <SelectTrigger data-testid="rule-severity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEVERITY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: opt.color }}
                      />
                      {opt.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Intervalo de repetição (min)</Label>
            <Input
              type="number"
              min={5}
              value={rule.repeat_interval_minutes || 5}
              onChange={(e) => handleChange('repeat_interval_minutes', Math.max(5, parseInt(e.target.value) || 5))}
              data-testid="rule-repeat-interval-input"
            />
          </div>
        </div>
        
        {/* Delete Button */}
        <Button 
          variant="ghost" 
          size="icon"
          className="text-destructive hover:bg-destructive/10 ml-2"
          onClick={onDelete}
          data-testid="rule-delete-btn"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Notification Settings */}
      <div className="space-y-4 pt-2 border-t border-border">
        {/* Info text */}
        <p className="text-xs text-muted-foreground italic">
          Todos os usuários cadastrados serão notificados automaticamente
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          {/* Email Notification */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Notificar por e-mail</Label>
              <Switch
                checked={rule.notify_email || false}
                onCheckedChange={(v) => handleChange('notify_email', v)}
                data-testid="rule-email-switch"
              />
            </div>
            {rule.notify_email && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Destinatários adicionais {!isAdmin && <span className="text-yellow-500">(somente admin)</span>}
                </Label>
                <Input
                  placeholder={isAdmin ? "email1@email.com, email2@email.com" : "Somente admin pode editar"}
                  value={(rule.notification_emails || []).join(', ')}
                  onChange={(e) => handleEmailsChange(e.target.value)}
                  className={cn("text-xs", !isAdmin && "opacity-50 cursor-not-allowed")}
                  disabled={!isAdmin}
                  data-testid="rule-emails-input"
                />
              </div>
            )}
          </div>
          
          {/* WhatsApp Notification */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Notificar por WhatsApp</Label>
              <Switch
                checked={rule.notify_whatsapp || false}
                onCheckedChange={(v) => handleChange('notify_whatsapp', v)}
                data-testid="rule-whatsapp-switch"
              />
            </div>
            {rule.notify_whatsapp && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Destinatários adicionais {!isAdmin && <span className="text-yellow-500">(somente admin)</span>}
                </Label>
                <PhoneListInput
                  value={rule.notification_phones || []}
                  onChange={(phones) => handleChange('notification_phones', phones)}
                  disabled={!isAdmin}
                  idPrefix="rule-phones"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { TRIGGER_OPTIONS, SEVERITY_OPTIONS };
export default GeofenceRuleEditor;
